import {
    normalizeRefinementItemResponse,
    normalizeStartRefinementResponse,
} from './utils/normalizers/rubric-refinement.normalizers'
import baseApi from './base.api'
import { reset as resetRubricRefinement, startOverRefinementProcess } from '../slices/rubric-refinement.slice'
import { rubricApi } from './rubric.api'
import { QUESTION_TAG } from './utils/tags'
import { getQuestionRubricById } from './utils/selectors/question.api.selectors'

export const rubricRefinementApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        startRefinement: builder.mutation({
            query: ({ questionRubricId }) => ({
                url: '/rubric-refinement/start',
                method: 'POST',
                params: { question_rubric_id: questionRubricId, process_type: 'MultipleSubmissions' },
                body: {},
            }),
            transformResponse: response => normalizeStartRefinementResponse(response),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data?.status === 'Initializing') {
                        dispatch(
                            baseApi.util.updateQueryData(
                                'fetchQuestions',
                                {
                                    token: arg.token,
                                },
                                draft => {
                                    const questionRubric = getQuestionRubricById(draft, Number(arg.questionRubricId))
                                    if (questionRubric) {
                                        questionRubric.processing = {
                                            progress: 10,
                                            message: 'Generating rubric refinement queue',
                                            status: 'ip',
                                        }
                                    }
                                },
                            ),
                        )
                    }
                } catch (error) {
                    console.error('Error starting refinement:', error)
                }
            },
        }),
        abortRefinement: builder.mutation({
            query: ({ id }) => ({
                url: `/rubric-refinement/${id}/abort`,
                method: 'POST',
                params: {},
                body: {},
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(resetRubricRefinement())
                } catch (error) {
                    console.error('Error aborting refinement:', error)
                }
            },
        }),
        finishRefinement: builder.mutation({
            query: ({ id }) => ({
                url: `/rubric-refinement/${id}/finish`,
                method: 'POST',
                params: {},
                body: {},
            }),
            transformResponse: response => ({
                versionId: response?.version_id,
                questionRubricId: response?.question_rubric_id,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    // Fetch updated processing progress for the questionRubric
                    dispatch(
                        rubricApi.endpoints.fetchRubricProgress.initiate({ questionRubricIds: [arg.questionRubricId] })
                    )
                } catch (error) {
                    console.error('Error finishing refinement:', error)
                }
            },
            invalidatesTags: [{ type: QUESTION_TAG, id: 'LIST' }],
        }),
        skipRefinementItem: builder.mutation({
            query: ({ refinementItemId }) => ({
                url: `/rubric-refinement/skip-item/${refinementItemId}`,
                method: 'POST',
                params: {},
                body: {},
            }),
            transformResponse: response => normalizeRefinementItemResponse(response),
        }),
        chooseRefinementCategory: builder.mutation({
            query: ({ refinementItemId, categoryId }) => ({
                url: `/rubric-refinement/select-item-pattern/${refinementItemId}`,
                method: 'POST',
                params: {},
                body: { user_decision: { pattern_id: categoryId } },
                timeout: 5 * 60 * 1000,
            }),
            transformResponse: response => normalizeRefinementItemResponse(response),
        }),
        confirmSuggestion: builder.mutation({
            query: ({ refinementItemId }) => ({
                url: `/rubric-refinement/confirm-suggestion/${refinementItemId}`,
                method: 'POST',
                params: {},
                body: {},
            }),
            transformResponse: response => normalizeRefinementItemResponse(response),
        }),
        changePreviousChoice: builder.mutation({
            query: ({ cleaningStepId, userDecision }) => ({
                url: `/rubric-refinement/cleaning-step/${cleaningStepId}/change-choice`,
                method: 'POST',
                params: {},
                body: { user_decision: userDecision },
            }),
        }),
        startOverRefinement: builder.mutation({
            query: ({ id }) => ({
                url: `/rubric-refinement/${id}/start-over`,
                method: 'POST',
                params: {},
                body: {},
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(startOverRefinementProcess())
                } catch (error) {
                    console.error('Error starting over refinement:', error)
                }
            },
        }),
        getNextRefinementItem: builder.query({
            query: ({ id }) => ({
                url: `/rubric-refinement/${id}/next-item`,
                method: 'GET',
            }),
            transformResponse: response => normalizeRefinementItemResponse(response),
        }),
    }),
})

export const {
    useStartRefinementMutation,
    useSkipRefinementItemMutation,
    useChooseRefinementCategoryMutation,
    useConfirmSuggestionMutation,
    useFinishRefinementMutation,
    useAbortRefinementMutation,
    useChangePreviousChoiceMutation,
    useStartOverRefinementMutation,
    useLazyGetNextRefinementItemQuery,
} = rubricRefinementApi
